import { useCallback, useEffect, useMemo, useState } from 'react';

interface PersistedState {
  open: boolean;
  width: number;
}

const DEFAULTS: PersistedState = { open: true, width: 280 };

/**
 * Persist a sidebar's open/closed flag + drag-resized width in
 * localStorage. Returns helpers shaped for direct binding to a
 * `<aside>` and a drag handle.
 *
 * The hook re-mirrors changes via the `storage` event so two browser
 * tabs of the same Bluebird stay in sync.
 */
export function usePersistentToggle(
  storageKey: string,
  defaults?: Partial<PersistedState>,
): {
  open: boolean;
  width: number;
  setOpen: (open: boolean) => void;
  setWidth: (width: number) => void;
  toggle: () => void;
} {
  const merged = useMemo<PersistedState>(
    () => ({ ...DEFAULTS, ...defaults }),
    [defaults],
  );

  const [state, setState] = useState<PersistedState>(() =>
    load(storageKey, merged),
  );

  // Mirror to localStorage on every change.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Best-effort persistence.
    }
  }, [storageKey, state]);

  // Cross-browser-tab sync.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      setState(load(storageKey, merged));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey, merged]);

  const setOpen = useCallback(
    (open: boolean) => setState((prev) => ({ ...prev, open })),
    [],
  );
  const setWidth = useCallback(
    (width: number) => setState((prev) => ({ ...prev, width })),
    [],
  );
  const toggle = useCallback(
    () => setState((prev) => ({ ...prev, open: !prev.open })),
    [],
  );

  return { open: state.open, width: state.width, setOpen, setWidth, toggle };
}

function load(key: string, defaults: PersistedState): PersistedState {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return {
      open:
        typeof parsed.open === 'boolean' ? parsed.open : defaults.open,
      width:
        typeof parsed.width === 'number' && parsed.width > 0
          ? parsed.width
          : defaults.width,
    };
  } catch {
    return defaults;
  }
}
